// Externals
import { useCallback, useMemo } from 'react';

// Libraries
import { NavGroup, NavItemType, type INavViewItem } from '@makemydeal/dr-sales-checkin-types';
import { getDealerLogoBaseUrl, getEnvironmentName } from '@makemydeal/dr-sales-checkin-ui-utils';
import { hostUrlHelper, locHrefUtil, SALES_CHECKIN_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import { useDealerDetailsSelector, useShowroomNavConfigSelector } from '@makemydeal/dr-sales-checkin-store';

// Behaviour layer
import { buildNavViewItems } from './showroomNav/buildNavViewItems';
import { useNavViewModel } from './showroomNav/useNavViewModel';

/**
 * Config-driven view model for the Showroom header (US2055174).
 *
 * Reads the unified `entities` slice and produces render-ready view items for the
 * top nav and the Share dropdown, plus the dealership branding config.
 *
 * @returns `topNavItems` (TOP_NAV, with the Share dropdown carrying its DROPDOWN menu items)
 * and `dealershipHeaderConfig` (logo + dealer name + logo click).
 */
export const useHeaderNavbarConfig = () => {
    const { entities, logoConfig } = useShowroomNavConfigSelector();
    const dealerDetails = useDealerDetailsSelector();
    const ctx = useNavViewModel();

    /** Full dealer logo URL for the current environment; empty when unset. */
    const getDealerLogoUrl = useCallback((): string => {
        const environment = hostUrlHelper.getHostUrlEnvironment(SALES_CHECKIN_APP_PREFIX, locHrefUtil.getLocHref());
        const environmentName = getEnvironmentName(environment).toLowerCase();
        return logoConfig.logoURL ? `${getDealerLogoBaseUrl(environmentName)}/${logoConfig.logoURL}` : '';
    }, [logoConfig]);

    const topNavItems = useMemo(() => {
        // Dropdown items are always Link view items — narrow so SendLinkDropdown gets `label` + `clickAction` typed.
        const dropdownItems = buildNavViewItems(entities, NavGroup.Dropdown, ctx).filter(
            (item): item is Extract<INavViewItem, { type: NavItemType.Link }> => item.type === NavItemType.Link
        );
        // The Share dropdown container carries its resolved menu items so the component gets them as a prop.
        return buildNavViewItems(entities, NavGroup.TopNav, ctx).map((item) =>
            item.type === NavItemType.ShareDropdown ? { ...item, items: dropdownItems } : item
        );
    }, [entities, ctx]);

    return {
        topNavItems,
        dealershipHeaderConfig: {
            logoUrl: getDealerLogoUrl(),
            dealerName: dealerDetails?.dealerName,
            onLogoClick: () => {
                // Refresh the app by reloading the current page.
                window.location.reload();
            }
        }
    };
};
